import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { Container, Heading, PageHeader } from '@bn-apps/dim-ui-react';
import { useAuth } from '@hooks/useAuth';
import tools, { toolGroupAnchors, type ToolConfig, type ToolGroup } from '@config/tools';
import ToolCard from './ToolCard';
import type { Tool } from '@/types';
import styles from './ToolsPage.module.scss';
import appStyles from '@/App.module.scss';

/**
 * ToolsPage component - Knowledge graph tool access
 * User Story 6: Access to search and explorer tools
 * User Story 7: Access to confidential tools (file browser, fuseki, sparql-anything, yasgui)
 */
export default function ToolsPage(): JSX.Element {
  const { t } = useTranslation();
  const { user } = useAuth();
  const location = useLocation();

  // Jump to the tool in the URL hash, also when the quick find links to it from /tools itself
  useEffect(() => {
    if (!location.hash) return;
    document.getElementById(decodeURIComponent(location.hash.slice(1)))?.scrollIntoView();
  }, [location.hash, location.key]);

  const canUse = (tool: ToolConfig): boolean =>
    (tool.requiredAccessLevel === 'projectMember' ? user?.isProjectMember : user?.isAuthenticated) === true;

  const toTool = (tool: ToolConfig): Tool => {
    const { group, nameKey, descriptionKey, ...rest } = tool;
    // SPARQL endpoints show whether the user can use them
    const status = group === 'sparql' ? (canUse(tool) ? 'available' : 'setup-pending') : rest.status;
    return { ...rest, name: t(nameKey), description: t(descriptionKey), status };
  };
  const toolsIn = (group: ToolGroup): Tool[] => tools.filter((tool) => tool.group === group).map(toTool);

  const publicTools = toolsIn('open');
  const sparqlTools = toolsIn('sparql');
  const confidentialTools = toolsIn('confidential');

  // Check if user can access confidential tools
  // isProjectMember is true if user has DIM-Project-Op or DIM-Project-Admin role
  const canAccessConfidential = user?.isProjectMember === true;

  return (
    <Container className={appStyles.pageContent}>
      <div className={styles.header}>
        <PageHeader title={t('tools.allTools')} lead={t('tools.description')} />
      </div>
      <div className="bn-mbe-2000">
        {/* Open Tools Section */}
        <section id={toolGroupAnchors.open} className={styles.group}>
          <div className={styles.toolsGrid}>
            {publicTools.map((tool) => (
              <ToolCard key={tool.id} tool={tool} />
            ))}
          </div>
        </section>

        {/* SPARQL Endpoints Section */}
        <section id={toolGroupAnchors.sparql} className={styles.group}>
          <Heading level={2} className={styles.groupTitle}>
            {t('tools.sparqlEndpoints')}
          </Heading>
          <div className={styles.toolsGrid}>
            {sparqlTools.map((tool) => (
              <ToolCard key={tool.id} tool={tool} />
            ))}
          </div>
        </section>

        {/* Confidential Tools Section */}
        {canAccessConfidential && (
          <section id={toolGroupAnchors.confidential} className={styles.lastGroup}>
            <Heading level={2} className={styles.groupTitle}>
              {t('tools.confidentialTools')}
            </Heading>
            <div className={styles.toolsGrid}>
              {confidentialTools.map((tool) => (
                <ToolCard key={tool.id} tool={tool} />
              ))}
            </div>
          </section>
        )}
      </div>
    </Container >
  );
}
